import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { BiTrash, BiPlus, BiFilterAlt } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";
import axiosClient from '../utils/apiClient.js';

const CaseListPi = ({ is_pi = false }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: "", serviceType: "" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const pageSize = 10;
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const roleName = user?.roleName;
  const isFieldAgent = roleName === "field_agent";
  const canManageCases = roleName === "admin" || roleName === "case_manager";

  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };


  const getStatusColor = (value) => {
    const v = (value || "").toLowerCase();

    if (["completed", "served", "closed"].includes(v)) return "bg-green-100 text-green-700";
    if (["in progress", "attempted"].includes(v)) return "bg-blue-100 text-blue-700";
    if (["pending", "assigned"].includes(v)) return "bg-yellow-100 text-yellow-700";
    if (["failed", "cancelled", "returned"].includes(v)) return "bg-red-100 text-red-700";

    return "bg-gray-100 text-gray-700";
  };

  useEffect(() => {
    if (roleName === "client") {
      //navigate("/client/cases/pi");
    }
  }, [roleName, navigate]);

  const fetchCases = async (page = 1) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication token not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    const queryParams = {
      page,
      pageSize,
      search: searchQuery,
      status: filters.status,
      serviceType: filters.serviceType,
      is_pi: is_pi,
    };

    try {
      const response = await axiosClient.get(`/pi-cases`, { params: queryParams });

      const data = response.data.data || [];
      console.log(data)
      setCases(data);
      setTotalPages(response.data.metadata?.totalPages || 1);
      setCurrentPage(response.data.metadata?.currentPage || 1);
    } catch (err) {
      setError("Failed to load cases. Please try again.");
      console.error("Error fetching cases:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIds([]);
    const handler = setTimeout(() => fetchCases(1), 300);
    return () => clearTimeout(handler);
  }, [searchQuery, filters]);

  useEffect(() => {
    setSelectedIds([]);
    fetchCases(currentPage);
  }, [currentPage]);

  const handlePageChange = (n) => {
    if (n > 0 && n <= totalPages) setCurrentPage(n);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (cases.length > 0 && selectedIds.length === cases.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(cases.map((c) => c.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected PI case(s)?`
      )
    )
      return;

    setIsDeleting(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/pi-cases/bulk-delete`,
        { caseIds: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedIds([]);
      fetchCases(currentPage);
    } catch (err) {
      console.error("Error bulk deleting PI cases:", err);
      try {
        const token = localStorage.getItem("token");
        await Promise.all(
          selectedIds.map((id) =>
            axios.delete(`${API_BASE_URL}/pi-cases/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          )
        );
        setSelectedIds([]);
        fetchCases(currentPage);
      } catch (fallbackErr) {
        setError(err.response?.data?.message || "Failed to delete selected cases.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete case "${name}"?`)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_BASE_URL}/pi-cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedIds((prev) => prev.filter((item) => item !== id));
      fetchCases(currentPage);
    } catch (err) {
      console.error("Error deleting case:", err);
      setError(err.response?.data?.message || "Failed to delete case.");
    }
  };


  return (
    <div className="p-1 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
          Private Investigation Cases
        </h1>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2 sm:gap-4">
          <input
            type="text"
            placeholder="Search by case, client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-4 py-2 border rounded-lg shadow-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {canManageCases && (
            <Link
              to="/cases/pi/new"
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition tour-create-case-btn"
            >
              <BiPlus className="mr-2" /> Create New Case
            </Link>
          )}
          {roleName == "client" && (
            <Link
              to="/cases/pi/new"
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition tour-create-case-btn"
            >
              <BiPlus className="mr-2" /> Create New Case
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center">Loading cases…</div>
      ) : error ? (
        <div className="p-6 text-center text-red-500">{error}</div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4 p-4 bg-gray-100 rounded-lg">
        <div className="flex items-center text-gray-600">
          <BiFilterAlt className="mr-2" size={22} /> Filters
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              name="status"
              value={filters.status}
              onChange={(e) =>
                setFilters((p) => ({ ...p, status: e.target.value }))
              }
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All</option>
              <option value="Researching">Researching</option>
              <option value="Active Surveillance">Active Surveillance</option>
              <option value="Report Pending">Report Pending</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk actions banner */}
      {canManageCases && selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>{selectedIds.length} of {cases.length} selected</span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition disabled:opacity-50"
          >
            <BiTrash className="mr-1 h-4 w-4" />
            {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
          </button>
        </div>
      )}

      {/* Table for large screens */}
      <div className="hidden sm:block bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {canManageCases && (
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={cases.length > 0 && selectedIds.length === cases.length}
                    onChange={handleSelectAll}
                    aria-label="Select all cases"
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
              )}
              {[
                "Case Title",
                "Case Number",
                "Field Work Status",
                "Status",
                "Client",
                "Assigned To",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cases.length ? (
              cases.map((c) => (
                <tr
                  key={c.id}
                  className={`hover:bg-gray-50 transition-colors duration-200 ${
                    selectedIds.includes(c.id) ? "bg-blue-50/50" : ""
                  }`}
                >
                  {canManageCases && (
                    <td className="px-4 py-4 text-sm w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => handleToggleSelect(c.id)}
                        aria-label={`Select case ${c.case_name}`}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {c.case_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {c.case_number}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(c.fieldwork_status)}`}>

                      {formatLabel(c.fieldwork_status) || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {c.status}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {c.is_agency ? c.agency : c.client_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {/* ✅ Display assigned user names */}
                    {c.assigned_to_names
                      ? c.assigned_to_names.split(",").join(", ")
                      : "Unassigned"}
                  </td>
                  {/* <td className="px-6 py-4 text-right text-sm font-medium space-x-6"> */}
                    <td className="px-6 py-4 text-sm font-medium">
                      <div className="flex items-center justify-end gap-4">

                    {canManageCases && !c.case_number?.startsWith('DEMO-') && (
                      <Link
                      to={
                        isFieldAgent
                          ? `/agent/cases/${c.id}`
                          : `/cases/pi/edit/${c.id}`
                      }
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </Link>
                    )}
                    <Link
                      to={
                        isFieldAgent
                          ? `/agent/cases/${c.id}`
                          : `/cases/pi/${c.id}`
                      }
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </Link>
                    {canManageCases && (
                      <button
                        onClick={() => handleDelete(c.id, c.case_name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <BiTrash className="inline-block h-5 w-5" />
                      </button>
                    )}
                      </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={canManageCases ? 8 : 7}
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No cases found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Cards for mobile */}
      <div className="sm:hidden space-y-4">
        {cases.length ? (
          cases.map((c) => (
            <div
              key={c.id}
              className={`border rounded-lg bg-white shadow-sm p-4 space-y-1 ${
                selectedIds.includes(c.id) ? "ring-2 ring-blue-500 bg-blue-50/30" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold text-gray-900">
                  {c.case_name}
                </div>
                {canManageCases && (
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(c.id)}
                    onChange={() => handleToggleSelect(c.id)}
                    aria-label={`Select case ${c.case_name}`}
                    className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                )}
              </div>
              <div className="text-sm text-gray-600">
                Case No: {c.case_number}
              </div>
              <div className="text-sm text-gray-600">
                Field Work Status	: {formatLabel(c.fieldwork_status) || "N/A"}
              </div>
              <div className="text-sm text-gray-600">Status: {c.status}</div>
              <div className="text-sm text-gray-600">
                Client: {c.client_name || "N/A"}
              </div>
              <div className="text-sm text-gray-600">
                {/* ✅ Assigned users on mobile */}
                Assigned:{" "}
                {c.assigned_to_names
                  ? c.assigned_to_names.split(",").join(", ")
                  : "Unassigned"}
              </div>
              <div className="flex justify-between pt-2">
                <Link
                  to={
                    isFieldAgent ? `/agent/cases/${c.id}` : `/cases/pi/${c.id}`
                  }
                  className="text-blue-600 hover:text-blue-900 flex items-center text-sm font-medium"
                >
                  View
                </Link>
                {canManageCases && (
                  <button
                    onClick={() => handleDelete(c.id, c.case_name)}
                    className="text-red-600 hover:text-red-900 flex items-center text-sm font-medium"
                  >
                    <BiTrash className="mr-1" /> Delete
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-sm text-gray-500">
            No cases found.
          </div>
        )}
      </div>


      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg w-full sm:w-auto ${currentPage === 1
                ? "bg-gray-200 text-gray-500"
                : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
          >
            Previous
          </button>
          <span className="text-gray-700 text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg w-full sm:w-auto ${currentPage === totalPages
                ? "bg-gray-200 text-gray-500"
                : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
          >
            Next
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default CaseListPi;
